/**
 * Shared AuthProvider — wraps app with MSAL.
 *
 * Handles MSAL initialization, redirect promise processing,
 * and active account management. Apps pass their config via props.
 */
import { MsalProvider } from '@azure/msal-react';
import {
  PublicClientApplication,
  EventType,
  type AuthenticationResult,
  type Configuration,
} from '@azure/msal-browser';
import { type ReactNode, useEffect, useState, useRef } from 'react';

interface AuthProviderProps {
  config: Configuration;
  children: ReactNode;
  /** Optional loading fallback. Defaults to a centered spinner. */
  loadingFallback?: ReactNode;
}

const defaultLoadingFallback = (
  <div
    style={{ display: 'flex', height: '100vh', alignItems: 'center', justifyContent: 'center' }}
  >
    <div style={{ opacity: 0.6 }}>Loading...</div>
  </div>
);

/**
 * Creates and initializes an MSAL PublicClientApplication instance.
 */
async function createAndInitMsal(config: Configuration): Promise<PublicClientApplication> {
  const instance = new PublicClientApplication(config);
  await instance.initialize();

  try {
    const response = await instance.handleRedirectPromise();
    if (response) {
      instance.setActiveAccount(response.account);
    }
  } catch {
    // Redirect handling failed — user will need to sign in again
  }

  instance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as AuthenticationResult;
      instance.setActiveAccount(payload.account);
    }
  });

  // If we have accounts but no active account, pick the first one
  const accounts = instance.getAllAccounts();
  if (accounts.length > 0 && !instance.getActiveAccount()) {
    instance.setActiveAccount(accounts[0]!);
  }

  return instance;
}

export function AuthProvider({ config, children, loadingFallback }: AuthProviderProps) {
  const [msalInstance, setMsalInstance] = useState<PublicClientApplication | null>(null);
  const initRef = useRef<Promise<PublicClientApplication> | null>(null);

  useEffect(() => {
    // Ensure we only init once, even in StrictMode
    if (!initRef.current) {
      initRef.current = createAndInitMsal(config);
    }
    void initRef.current.then(setMsalInstance);
  }, [config]);

  if (!msalInstance) {
    return (loadingFallback ?? defaultLoadingFallback) as JSX.Element;
  }

  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}
